import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import styled from "styled-components";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import StandardHeader from "components/global/StandardHeader";
import Table from "../../components/Table";
import { AppBar, Box, Divider as MuiDivider, Tab, Tabs, TextField, Typography, InputAdornment, IconButton, Tooltip, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { NotInterestedOutlined } from "@mui/icons-material";
import { spacing } from "@mui/system";
import {
  getContractInvoices,
  getContractInvoiceAccounts,
  getSignedUrl,
} from "../../modules/contractInvoices/api/actions";

const Divider = styled(MuiDivider)(spacing);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contract-invoice-tabpanel-${index}`}
      aria-labelledby={`contract-invoice-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `contract-invoice-tab-${index}`,
    "aria-controls": `contract-invoice-tabpanel-${index}`,
  };
}

function ContractInvoiceDownloadMenu({ data, tooltip, getSignedUrlFn }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const hasPdf = data?.pdfUrl && data.pdfUrl.trim() !== "";
  const excelKeyOrUrl = data?.excelUrl && data.excelUrl.trim() !== "" ? data.excelUrl.trim() : "";
  const hasExcel = excelKeyOrUrl !== "";

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadPdf = () => {
    if (!data?.pdfUrl) {
      handleClose();
      return;
    }
    handleClose();
    const pdfKeyOrUrl = data.pdfUrl.trim();
    if (pdfKeyOrUrl.startsWith("http://") || pdfKeyOrUrl.startsWith("https://")) {
      window.open(pdfKeyOrUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (getSignedUrlFn) {
      getSignedUrlFn(pdfKeyOrUrl)
        .then(({ signedUrl }) => {
          if (signedUrl) window.open(signedUrl, "_blank", "noopener,noreferrer");
        })
        .catch((err) => {
          console.error("Failed to get PDF signed URL:", err);
        });
    }
  };

  const handleDownloadCsv = () => {
    if (!hasExcel) {
      handleClose();
      return;
    }
    handleClose();
    if (excelKeyOrUrl.startsWith("http://") || excelKeyOrUrl.startsWith("https://")) {
      window.open(excelKeyOrUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (getSignedUrlFn) {
      getSignedUrlFn(excelKeyOrUrl)
        .then(({ signedUrl }) => {
          if (signedUrl) window.open(signedUrl, "_blank", "noopener,noreferrer");
        })
        .catch((err) => {
          console.error("Failed to get CSV signed URL:", err);
        });
    }
  };

  return (
    <>
      <Tooltip title={tooltip || "Download"}>
        <span>
          <IconButton
            onClick={handleOpen}
            size="small"
            sx={{ color: "primary.main" }}
            aria-controls={open ? "download-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            disabled={!hasPdf && !hasExcel}
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id={`download-menu-${data?.id ?? data?.uuid ?? ""}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "download-button" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleDownloadPdf} disabled={!hasPdf}>
          Download Invoice
        </MenuItem>
        <MenuItem onClick={handleDownloadCsv} disabled={!hasExcel}>
          Download CSV
        </MenuItem>
      </Menu>
    </>
  );
}

ContractInvoiceDownloadMenu.propTypes = {
  data: PropTypes.object,
  tooltip: PropTypes.string,
  getSignedUrlFn: PropTypes.func,
};

const invoiceAccountColumns = [
  { name: "ID", field: "id", type: "text" },
  { name: "INVOICE ID", field: "contractInvoiceId", type: "text" },
  { name: "ACCOUNT #", field: "accountNum", type: "text" },
  { name: "UNIT ID", field: "unitId", type: "text" },
  { name: "UNIT ADDRESS", field: "unitAddress", type: "text" },
  { name: "BALANCE", field: "balance", type: "money", sortable: true },
];

const ContractInvoicesPage = (props) => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("balance");
  const [sortOrder, setSortOrder] = useState("desc");
  const [accountSortBy, setAccountSortBy] = useState("balance");
  const [accountSortOrder, setAccountSortOrder] = useState("desc");
  const isInitialMount = useRef(true);
  const isAccountInitialMount = useRef(true);

  const invoiceColumns = [
    { name: "PM NAME", field: "propertyManagerName", type: "link", linkRoute: "/propertyManager", linkParams: ["propertyManagerUuid", "propertyManagerId"] },
    { name: "PROPERTY NAME", field: "propertyName", type: "link", linkRoute: "/property", linkParams: ["propertyUuid", "propertyId", "propertyManagerId"] },
    { name: "BALANCE", field: "balance", type: "money", sortable: true },
    { 
      name: "EMAILED", 
      field: "emailed", 
      type: "icons", 
      icons: [
        {
          icon: ({ data }) => {
            const value = data.emailed;
            if (value === 1) {
              return <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: '1.5rem' }} />;
            } else if (value === 0) {
              return <NotInterestedOutlined sx={{ color: 'error.main', fontSize: '1.5rem' }} />;
            }
            return null;
          },
          renderCondition: (data) => data.emailed !== undefined && data.emailed !== null,
        }
      ]
    },
    { name: "DUE DATE", field: "dueDate", type: "date", sortable: true },
    { 
      name: "DOWNLOAD", 
      field: "", 
      type: "icons", 
      icons: [
        {
          icon: ({ data, tooltip }) => (
            <ContractInvoiceDownloadMenu
              data={data}
              tooltip={tooltip}
              getSignedUrlFn={getSignedUrl}
            />
          ),
          tooltip: "Download",
          renderCondition: () => true,
        }
      ]
    },
  ];

  useEffect(() => {
    document.title = "Ubil+ Contract Invoices";
    // Initial load - fetch invoices with default sort
    const order = sortOrder.toUpperCase();
    props.getContractInvoices(true, sortBy, order);
    // Initial load - fetch invoice accounts with default sort
    const accountOrder = accountSortOrder.toUpperCase();
    props.getContractInvoiceAccounts(true, accountSortBy, accountOrder);
    isInitialMount.current = false;
    isAccountInitialMount.current = false;
  }, []);

  // Refetch invoices when sort changes (but not on initial mount)
  useEffect(() => {
    if (!isInitialMount.current && sortBy) {
      const order = sortOrder.toUpperCase();
      props.getContractInvoices(false, sortBy, order);
    }
  }, [sortBy, sortOrder]);

  // Refetch invoice accounts when sort changes (but not on initial mount)
  useEffect(() => {
    if (!isAccountInitialMount.current && accountSortBy) {
      const order = accountSortOrder.toUpperCase();
      props.getContractInvoiceAccounts(false, accountSortBy, order);
    }
  }, [accountSortBy, accountSortOrder]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const { contractInvoices, contractInvoiceAccounts, isLoading } =
    props.contractInvoicesState;

  // Filter invoices based on search query
  const filterInvoices = (invoices, query) => {
    if (!query) return invoices;
    const lowerQuery = query.toLowerCase();
    return invoices.filter((invoice) => {
      return (
        (invoice.id && invoice.id.toString().toLowerCase().includes(lowerQuery)) ||
        (invoice.propertyManagerName && invoice.propertyManagerName.toLowerCase().includes(lowerQuery)) ||
        (invoice.propertyName && invoice.propertyName.toLowerCase().includes(lowerQuery)) ||
        (invoice.balance && invoice.balance.toString().toLowerCase().includes(lowerQuery))
      );
    });
  };

  // Filter invoice accounts based on search query
  const filterInvoiceAccounts = (accounts, query) => {
    if (!query) return accounts;
    const lowerQuery = query.toLowerCase();
    return accounts.filter((account) => {
      return (
        (account.id && account.id.toString().toLowerCase().includes(lowerQuery)) ||
        (account.contractInvoiceId && account.contractInvoiceId.toString().toLowerCase().includes(lowerQuery)) ||
        (account.accountNum && account.accountNum.toString().toLowerCase().includes(lowerQuery)) ||
        (account.unitId && account.unitId.toString().toLowerCase().includes(lowerQuery)) ||
        (account.unitAddress && account.unitAddress.toLowerCase().includes(lowerQuery)) ||
        (account.balance && account.balance.toString().toLowerCase().includes(lowerQuery))
      );
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleAccountSort = (field) => {
    if (accountSortBy === field) {
      setAccountSortOrder(accountSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setAccountSortBy(field);
      setAccountSortOrder('asc');
    }
  };

  const filteredInvoices = filterInvoices(contractInvoices || [], searchQuery);
  const filteredInvoiceAccounts = filterInvoiceAccounts(contractInvoiceAccounts || [], searchQuery);

  return (
    <NavFrame page={"Contract Invoices"}>
      <StandardHeader title="Contract Invoices" />
      <Divider className="mb-3 mt-3" />
      <AppBar position="static" color="primary">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="Contract Invoices Tabs"
        >
          <Tab label="Contract Invoices" {...a11yProps(0)} />
          <Tab label="Contract Invoice Accounts" {...a11yProps(1)} />
        </Tabs>
      </AppBar>
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider', mr: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
            Search Contract Invoices
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider', ml: 1 }} />
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="account holder name, number..."
          value={searchQuery}
          onChange={handleSearchChange}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Box>
      <TabPanel value={tabValue} index={0}>
        <Table
          paginated={true}
          columns={invoiceColumns.map(col => ({
            ...col,
            onSort: col.sortable ? () => handleSort(col.field) : undefined,
            sortDirection: col.sortable && sortBy === col.field ? sortOrder : false,
          }))}
          data={filteredInvoices}
          isLoading={isLoading}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Table
          paginated={true}
          columns={invoiceAccountColumns.map(col => ({
            ...col,
            onSort: col.sortable ? () => handleAccountSort(col.field) : undefined,
            sortDirection: col.sortable && accountSortBy === col.field ? accountSortOrder : false,
          }))}
          data={filteredInvoiceAccounts}
          isLoading={isLoading}
        />
      </TabPanel>
    </NavFrame>
  );
};

ContractInvoicesPage.propTypes = {
  contractInvoicesState: PropTypes.object.isRequired,
  getContractInvoices: PropTypes.func.isRequired,
  getContractInvoiceAccounts: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    contractInvoicesState: state.contractInvoices,
  };
}

export default connect(mapStateToProps, {
  getContractInvoices,
  getContractInvoiceAccounts,
})(ContractInvoicesPage);
